/**
 * Wishlist
 * ======================================
 * - localStorage as primary storage (works for guests)
 * - syncs with server for logged-in users via ajax/index.php
 * - renders product cards on .js-wishlist-page
 */

const STORAGE_KEY = 'woo-wishlist'
const BTN_SELECTOR = '.js-wishlist-btn'
const COUNT_SELECTOR = '.js-wishlist-count'
const PAGE_SELECTOR = '.js-wishlist-page'
const AJAX_URL = '/app/themes/ipress/ajax/index.php'
const ACTIVE_CLASS = 'is-active'

class Wishlist {
  constructor() {
    this.ids = this._loadLocal()

    this._updateUI()
    this._bindClicks()

    window.addEventListener('load', this._onLoad)
  }

  // ---- Storage ----

  _loadLocal() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []
    } catch {
      return []
    }
  }

  _saveLocal() {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.ids))
  }

  // ---- Init ----

  _onLoad = () => {
    if (window.isLoggedIn) {
      this._ajax({ action: 'Wishlist_get' }, (res) => {
        if (res && Array.isArray(res.ids)) {
          // merge: server is source of truth, but keep local-only items
          const merged = [...new Set([...res.ids, ...this.ids])]
          this.ids = merged
          this._saveLocal()
          this._updateUI()
        }
      })
    }

    const page = document.querySelector(PAGE_SELECTOR)
    if (page) {
      this._renderPage(page)
    }
  }

  // ---- Toggle ----

  _toggle(productId) {
    const idx = this.ids.indexOf(productId)
    if (idx !== -1) {
      this.ids.splice(idx, 1)
    } else {
      this.ids.push(productId)
    }
    this._saveLocal()
    this._updateUI()

    if (window.isLoggedIn) {
      this._ajax({ action: 'Wishlist_toggle', product_id: productId }, () => {})
    }
  }

  // ---- Click binding ----

  _bindClicks() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest(BTN_SELECTOR)
      if (!btn) return
      e.preventDefault()
      e.stopPropagation()
      const id = parseInt(btn.dataset.id, 10)
      if (id) this._toggle(id)
    })
  }

  // ---- UI ----

  _updateUI() {
    // counter
    document.querySelectorAll(COUNT_SELECTOR).forEach(el => {
      el.textContent = this.ids.length > 0 ? this.ids.length : ''
    })

    // button active states
    document.querySelectorAll(BTN_SELECTOR).forEach(btn => {
      const id = parseInt(btn.dataset.id, 10)
      btn.classList.toggle(ACTIVE_CLASS, this.ids.includes(id))
    })
  }

  // ---- Wishlist page render ----

  _renderPage(container) {
    if (!this.ids.length) {
      container.innerHTML = '<p class="wishlist__empty">Your collection is empty.</p>'
      return
    }

    container.innerHTML = '<div class="wishlist__loading">Loading…</div>'

    this._ajax({ action: 'Wishlist_products', ids: this.ids }, (res) => {
      if (!res || !Array.isArray(res.products) || !res.products.length) {
        container.innerHTML = '<p class="wishlist__empty">Your collection is empty.</p>'
        return
      }

      container.innerHTML = res.products.map(p => this._buildItemHtml(p)).join('')
      this._updateUI()
    })
  }

  _buildItemHtml(p) {
    const badge = p.sold
      ? `<div class="sale-badge sale-badge--sold">Sold</div>`
      : `<div class="sale-badge">for sale | ${p.price_html}</div>`

    const brandPart = p.brand ? `, ${p.brand}` : ''

    const wishlistBtn = `<button type="button" class="artworks__wishlist js-wishlist-btn" data-id="${p.id}" aria-label="Remove from wishlist">
        <svg class="icon icon--heart"><use xlink:href="#icon--heart"></use></svg>
      </button>`

    return `<div class="artworks__item artworks__item--woo">
      <div class="artworks__item-bg">
        <a class="artworks__item-link" href="${p.url}">
          ${p.image_url ? `<img src="${p.image_url}" alt="${p.name}" loading="lazy">` : ''}
        </a>
        ${wishlistBtn}
      </div>
      <a class="artworks__item-link artworks__item-link--name" href="${p.url}">
        <div class="artworks__name">${p.name}${brandPart}</div>
      </a>
      ${badge}
    </div>`
  }

  // ---- AJAX helper ----

  _ajax(data, callback) {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', AJAX_URL, true)
    xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded')

    const body = Object.entries(data)
      .map(([k, v]) => {
        if (Array.isArray(v)) {
          return v.map(id => `${encodeURIComponent(k + '[]')}=${encodeURIComponent(id)}`).join('&')
        }
        return `${encodeURIComponent(k)}=${encodeURIComponent(v)}`
      })
      .join('&')

    xhr.send(body)

    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4 && xhr.status === 200) {
        try {
          callback(JSON.parse(xhr.responseText))
        } catch {
          console.error('Wishlist AJAX parse error', xhr.responseText)
        }
      }
    }
  }
}

new Wishlist()
